import React from "react";
import Card from "components/card";
import Button from "components/button/Button";
import DropzoneFile from "components/dropzone";
import InputField from "components/fields/InputField";
import SwitchField from "components/fields/SwitchField";
import { AxiosResponse } from "axios";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { addBanner, getBanner, updateBanner } from "api/BannerAPI";

import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

const BannerForm: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const [defaultImg, setDefaultImg] = React.useState<string>("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({});

  const dropFile = (files: File[]) => {
    setFiles([...files]);
  };

  const removeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const arrangeFiles = (files: File[]) => {
    setFiles([...files]);
  };

  const changeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    setIsLoading(true);

    if (data.link === "") delete data.link;

    const payload = {
      ...data,
      is_active: true,
      is_main: false,
      name: data?.name,
      link: data?.link,
      image: files.length > 0 ? files[0] : null,
    };

    payload.image === null && delete payload.image;

    let promise = null;
    if (!slug) promise = addBanner(payload);
    else promise = updateBanner(slug, payload);

    toast.promise(promise, {
      loading: "Menyimpan data banner...",
      success: (res: AxiosResponse) => {
        navigate("/admin/banners");
        setIsLoading(false);
        return "Berhasil menyimpan data banner!";
      },
      error: (err: any) => {
        setIsLoading(false);
        return err?.response?.data?.message || "Gagal menyimpan data banner!";
      },
    });
  };

  const getDetailData = (slug: string): void => {
    const promise = getBanner(slug);

    toast.promise(promise, {
      loading: "Mengambil data banner...",
      success: (res) => {
        if (res.status === 200) {
          delete res.data.data.created_at;
          delete res.data.data.updated_at;
          delete res.data.data.id;
          delete res.data.data.slug;

          reset({
            ...res.data.data,
          });

          if (res.data.data.image) setDefaultImg(res.data.data.image);

          return res.data.message || "Berhasil mengambil data banner!";
        }
      },
      error: (err) => {
        console.error(err);
        return err?.response?.data?.message || "Gagal mengambil data banner!";
      },
    });
  };

  React.useEffect(() => {
    if (slug) getDetailData(slug);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6 pb-3 mt-3"}>
      <header className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Tambah Banner
        </div>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
        <div className="mt-8">
          <div className="flex flex-col">
            <label htmlFor="images" className="label">
              Foto Banner
            </label>
            <DropzoneFile
              text="Foto Banner"
              accept={{
                "image/*": [".jpg", ".png", ".jpeg", ".webp"],
              }}
              maxSize={2048000}
              maxFiles={1}
              files={files}
              dropFile={(files: File[]) => dropFile(files)}
              removeFile={(files: File[]) => removeFile(files)}
              arrangeFiles={(files: File[]) => arrangeFiles(files)}
              changeFile={(files: File[]) => changeFile(files)}
            />
          </div>
          {defaultImg && (
            <div className="my-3 flex flex-col">
              <label htmlFor="images" className="label">
                Foto Lama Banner
              </label>
              <div className="mx-auto flex h-48 w-auto flex-col items-center justify-center rounded-md border border-gray-300">
                <img
                  src={`${process.env.REACT_APP_PUBLIC_URL}/` + defaultImg}
                  alt="banner"
                  className="h-full w-full rounded-md object-cover"
                />
              </div>
            </div>
          )}
          <p className="mb-3 ml-3 mt-7 font-medium text-gray-600">
            Detail Banner
          </p>
          <div className="flex items-center gap-3">
            <div className="w-fit px-3">
              <SwitchField
                label="Aktifkan Banner"
                desc=""
                id="is_active"
                mb="mb-3"
                mt=""
                placeholder="Aktifkan Banner"
                register={register}
                errors={errors}
                validationSchema={{}}
              />
            </div>
            <div className="w-fit px-3">
              <SwitchField
                label="Jadikan Banner Utama"
                desc=""
                id="is_main"
                mb="mb-3"
                mt=""
                placeholder="Jadikan Banner Utama"
                register={register}
                errors={errors}
                validationSchema={{}}
              />
            </div>
          </div>
          <InputField
            extra="mb-3"
            label="Nama Banner"
            placeholder="Masukkan nama banner"
            id="name"
            type="text"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{
              required: "Nama Banner harus diisi!",
            }}
          />
          <InputField
            extra="mb-3"
            label="Link"
            placeholder="Masukkan link banner"
            id="link"
            type="text"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{
              required: "Link Banner harus diisi!",
            }}
          />
        </div>
        <Button
          children="Simpan"
          size="fit"
          variant="primary"
          extra="mt-7 mb-5"
          isLoading={isLoading}
        />
      </form>
    </Card>
  );
};

export default BannerForm;
